import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Trash2, ChevronRight, Search, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { listQuotes, deleteQuote } from "@/lib/api";

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [oversizedOnly, setOversizedOnly] = useState(false);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const load = (q) => {
    setLoading(true);
    listQuotes(q)
      .then(setQuotes)
      .catch(() => toast.error("Failed to load quotes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(debounced);
  }, [debounced]);

  // A quote is "oversized" when at least one of its rooms exceeded the
  // largest pricing tier — pricing.calculate_quote stamps room.oversized.
  const isOversized = (q) => (q.rooms || []).some((r) => r.oversized);
  const visibleQuotes = oversizedOnly ? quotes.filter(isOversized) : quotes;
  const oversizedCount = quotes.filter(isOversized).length;

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this quote?")) return;
    try {
      await deleteQuote(id);
      toast.success("Quote deleted");
      load(debounced);
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="mb-8">
        <div className="label-xs">Module 03 / Archive</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          QUOTE HISTORY
        </h1>
        <p className="mt-3 text-sm text-zinc-600 font-mono">
          {loading
            ? "Loading..."
            : oversizedOnly
              ? `${visibleQuotes.length} oversized quote(s)`
              : debounced
                ? `${quotes.length} result(s) for "${debounced}"`
                : `${quotes.length} quote(s) saved${oversizedCount > 0 ? ` · ${oversizedCount} oversized` : ""}`}
        </p>
      </div>

      {/* Search bar */}
      <div
        className="mb-3 border border-zinc-200 flex items-center bg-white"
        data-testid="quotes-search"
      >
        <div className="px-4 border-r border-zinc-200 py-3">
          <Search size={16} className="text-zinc-400" />
        </div>
        <input
          type="text"
          className="flex-1 px-4 py-3 font-mono text-sm outline-none bg-transparent"
          placeholder="Search by customer name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="quotes-search-input"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="px-4 text-zinc-400 hover:text-zinc-900"
            data-testid="quotes-search-clear"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filter toggles */}
      {oversizedCount > 0 && (
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setOversizedOnly((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-2 border text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors ${
              oversizedOnly
                ? "bg-[#ff5722] text-white border-[#ff5722]"
                : "bg-white text-[#ff5722] border-zinc-200 hover:border-[#ff5722]"
            }`}
            data-testid="oversized-filter-toggle"
            title="Quotes where at least one room exceeded the largest pricing tier"
          >
            <AlertTriangle size={12} />
            {oversizedOnly
              ? `Showing ${oversizedCount} oversized · clear filter`
              : `${oversizedCount} oversized quote${oversizedCount === 1 ? "" : "s"}`}
            {oversizedOnly && <X size={12} />}
          </button>
        </div>
      )}

      {!loading && visibleQuotes.length === 0 && (
        <div className="border border-zinc-200 p-16 text-center blueprint-grid">
          <FileText size={32} className="mx-auto text-zinc-400" />
          <div className="font-display text-2xl font-black mt-4 tracking-tighter">
            {oversizedOnly
              ? "NO OVERSIZED QUOTES"
              : debounced
                ? "NO MATCHES"
                : "NO QUOTES YET"}
          </div>
          <div className="label-xs mt-2">
            {oversizedOnly
              ? "All quotes are within their pricing tiers"
              : debounced
                ? "Try a different name"
                : "Upload a sketch to start"}
          </div>
          {!debounced && !oversizedOnly && (
            <button
              onClick={() => navigate("/")}
              className="btn-sharp mt-6"
              data-testid="empty-upload-btn"
            >
              Go to Upload
            </button>
          )}
        </div>
      )}

      {visibleQuotes.length > 0 && (
        <div className="border border-zinc-200" data-testid="quotes-list">
          {visibleQuotes.map((q, i) => {
            const oversized = isOversized(q);
            return (
            <div
              key={q.id}
              className={`w-full flex items-center justify-between hover:bg-[#fafafa] transition-colors ${
                i < visibleQuotes.length - 1 ? "border-b border-zinc-200" : ""
              }`}
              data-testid={`quote-item-${i}`}
            >
              <button
                type="button"
                onClick={() => navigate(`/quotes/${q.id}`)}
                className="flex-1 min-w-0 text-left p-5"
                data-testid={`quote-open-${i}`}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-xl font-black tracking-tight">
                    {q.client_name || "(Unnamed)"}
                  </span>
                  {q.area && (
                    <span className="label-xs px-2 py-0.5 bg-[#f4f4f5] border border-zinc-200">
                      {q.area}
                    </span>
                  )}
                  {q.color && (
                    <span className="label-xs px-2 py-0.5 bg-[#f4f4f5] border border-zinc-200">
                      {q.color}
                    </span>
                  )}
                  {q.produced_at ? (
                    <span className="label-xs px-2 py-0.5 bg-[#16a34a] text-white !text-white border border-[#16a34a]">
                      DONE
                    </span>
                  ) : q.confirmed_at ? (
                    <span className="label-xs px-2 py-0.5 bg-[#005bea] text-white !text-white border border-[#005bea]">
                      CONFIRMED
                    </span>
                  ) : null}
                  {q.scheduled_cut_date && !q.produced_at ? (
                    <span
                      className="label-xs px-2 py-0.5 bg-[#7c3aed] text-white !text-white border border-[#7c3aed]"
                      data-testid={`quote-scheduled-${i}`}
                    >
                      CUT {q.scheduled_cut_date}
                    </span>
                  ) : null}
                  {oversized ? (
                    <span
                      className="label-xs px-2 py-0.5 bg-[#ff5722] text-white !text-white border border-[#ff5722] inline-flex items-center gap-1"
                      data-testid={`quote-oversized-${i}`}
                      title="At least one room exceeded the largest pricing tier — review the price"
                    >
                      <AlertTriangle size={10} /> OVERSIZED
                    </span>
                  ) : null}
                </div>
                <div className="font-mono text-xs text-zinc-500 mt-2">
                  {q.rooms.length} room(s) · {new Date(q.created_at).toLocaleString()}
                </div>
              </button>
              <div className="flex items-center gap-2 sm:gap-4 pr-3 sm:pr-5">
                <div className="font-display text-2xl font-black tracking-tighter tabular-nums text-[#005bea]">
                  €{(q.total || 0).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={(e) => remove(q.id, e)}
                  onTouchEnd={(e) => {
                    // Belt-and-braces for iPad Safari — click sometimes gets
                    // swallowed by an adjacent navigation handler.
                    e.stopPropagation();
                  }}
                  className="text-zinc-400 hover:text-[#ff5722] active:text-[#ff5722] p-3 -m-1 border border-transparent hover:border-[#ff5722] active:border-[#ff5722] transition-colors"
                  data-testid={`quote-delete-${i}`}
                  aria-label="Delete quote"
                  title="Delete this quote"
                >
                  <Trash2 size={18} />
                </button>
                <ChevronRight size={16} className="text-zinc-400 shrink-0" />
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Quotes;
